import * as queryString from 'query-string';
import {API as API_URL} from './config';
import {CONNECTION_REFUSED} from './API-errors';

interface IResponse {
  status: number;
  statusText: string;
  json: () => Promise<any>;
}

const error = (name, response) => {
  const err: any = new Error(name);
  err.response = response;
  return err;
};

const handleErrors = (err) => {
  /**
   * @see https://developer.mozilla.org/en/docs/Web/API/Fetch_API/Using_Fetch#Checking_that_the_fetch_was_successful
   */
  throw (err instanceof TypeError)
    ? error(err.message, {errors: [CONNECTION_REFUSED]})
    : err;
};

function checkStatus(response: IResponse): (IResponse | Promise<any>) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  return Promise.resolve()
    .then(() => response.json())
    .then(parsedResponse => {
      if (response.status === 401 && parsedResponse.redirectUrl) {
        window.location.href = parsedResponse.redirectUrl;
      }

      throw error(response.statusText, parsedResponse);
    });
}

function parseJSON(response: IResponse): any {
  return response.json();
}

function get(url: string, params: any = {}): Promise<any> {
  const query = queryString.stringify(
    {...params, ...queryString.parse(window.location.search)},
    {arrayFormat: 'bracket'},
  );
  const newUrl = `${API_URL}${url}${query ? '?' : ''}${query}`;

  return fetch(newUrl, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
  })
  .then(checkStatus)
  .then(parseJSON)
  .catch(handleErrors);
}

const send = (url: string, params: any, method: string): Promise<any> => (
  fetch(buildUrl(url), {
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    method,
    body: JSON.stringify(params),
  })
  .then(checkStatus)
  .then(parseJSON)
  .catch(handleErrors)
);

const deleteJSON = (url: string, params: any): Promise<any> => send(url, params, 'DELETE');
const patchJSON = (url: string, params: any): Promise<any> => send(url, params, 'PATCH');
const postJSON = (url: string, params: any): Promise<any> => send(url, params, 'POST');
const putJSON = (url: string, params: any): Promise<any> => send(url, params, 'PUT');

function postForm(url: string, formData: FormData): Promise<any> {
  return fetch(buildUrl(url), {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  })
  .then(checkStatus)
  .then(parseJSON)
  .catch(handleErrors);
}

function buildUrl(url: string): string {
  return `${API_URL}${url}${window.location.search || ''}`;
}

export const API = {
  deleteJSON,
  patchJSON,
  postJSON,
  putJSON,
  get,
  postForm,
};
